import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Label, Container } from 'semantic-ui-react';
import i18next from 'i18next';
import { fetchActiveOrders } from '../../../actions';
import { getActiveOrders, getCurrentRestaurant } from '../../../selectors';
import { formatPrice } from '../../../helpers/format';
import { DateRangePicker, DataGrid } from '../../common';
import { updateUrlState } from '../../../helpers/url';
import { ORDERS_UPDATE_INTERVAL } from '../../../config';

import './Orders.scss';

const Orders = (props) => {
    const getDatesFromQuery = () => {
        const params = new URLSearchParams(props.location.search);

        return { start: params.get('startDate'), end: params.get('endDate') };
    };
    const { start, end } = getDatesFromQuery();
    const { toggleSoundNotifications, history } = props;
    const [startDate, setStartDate] = useState(start ? moment(start) : moment().startOf('day'));
    const [endDate, setEndDate] = useState(end ? moment(end) : moment().endOf('day'));
    const dispatch = useDispatch();
    const restaurant = useSelector(getCurrentRestaurant);
    const orders = useSelector(getActiveOrders);
    const { ordersUpdateDate, muteSoundNotifications } = useSelector(state => state.businessApp.orderCenter);
    const isRtl = i18next.dir() === 'rtl';

    useEffect(() => {
        if (restaurant.id) {
            dispatch(fetchActiveOrders(restaurant.id, startDate, endDate));
        }
    }, [startDate, endDate]);

    useEffect(() => {
        updateUrlState(history, history.location, { pageSize: 50 });
    }, [updateUrlState]);

    const handleOrderSelected = (order) => {
        history.push(`/restaurant/${restaurant.id}/orders/${order.id}`);
    };

    const columns = [{
        Header: i18next.t('order'),
        maxWidth: 150,
        accessor: 'id',
        filterable: true
    }, {
        id: 'createdAt',
        Header: i18next.t('receiveTime'),
        accessor: order => moment(order.createdAt).format('DD/MM/YYYY HH:mm:ss'),
        width: 250,
        filterable: true
    }, {
        id: 'orderType',
        Header: i18next.t('orderType'),
        width: 250,
        accessor: order => i18next.t(order.cartType || 'payment'),
        filterable: true
    }, {
        id: 'customer',
        Header: i18next.t('customer'),
        accessor: order => order.payments.map(({ customer }) => [customer.name || customer.alias, customer.phone].filter(c => !!c).join(', ')).join(', '),
        filterable: true
    }, {
        id: 'payment',
        Header: i18next.t('payment'),
        width: 250,
        accessor: order => {
            const total = formatPrice(order.totalPayment, restaurant.currencyCode, 2);
            let methods = '';
            if (order.orderType === 'kiosk') {
                methods = 'Kiosk';
                if (order.totalPayment > 0)
                    methods += ` (${order.payments.reduce((methods, p) => methods.concat((p.transactions || []).map(t => t.paymentMethod).join(', ')), '')})`;
            } else {
                methods = 'App';
            }

            return `${methods} ${total}`;
        },
        filterable: true
    }, {
        id: 'actions',
        Header: i18next.t('actions'),
        width: 200,
        sortable: false,
        filterable: false,
        Cell(row) {
            const order = row.original;

            return (
                <div>
                    <Button
                        circular
                        size='tiny'
                        icon='find'
                        primary
                        onClick={() => handleOrderSelected(order)}
                    />
                </div>
            );
        }
    }];

    return (
        <div style={{ padding: 10 }}>
            <div className="orders-center-actions">
                <Button
                    content={i18next.t('subscriptions')}
                    floated="right"
                    onClick={() => history.push(`/restaurant/${restaurant.id}/subscriptions`)}
                    basic
                />
                <Container className='pi-orders-options-days-back'>
                    {i18next.t("orders.dateRange")}
                    <DateRangePicker
                        value={[startDate, endDate]}
                        onChange={(rangeDates) => {
                            const start = rangeDates[0].startOf('day');
                            const end = rangeDates[1].endOf('day');
                            setStartDate(start);
                            setEndDate(end);
                            updateUrlState(history, history.location, { startDate: start, endDate: end });
                            dispatch(fetchActiveOrders(restaurant.id, start, end));
                        }}
                       
                        enableFutureDate
                    />
                </Container>
                <Button
                    content={i18next.t('refresh')}
                    icon='refresh'
                    onClick={() => dispatch(fetchActiveOrders(restaurant.id, startDate, endDate))}
                    basic
                    style={{
                        [isRtl ? "marginRight" : "marginLeft"]: 20
                    }}
                />
                <Button
                    color={muteSoundNotifications ? 'blue' : 'red'}
                    content={i18next.t(`orders.${muteSoundNotifications ? 'unmute' : 'mute'}`)}
                    icon={`alarm${!muteSoundNotifications ? ' mute' : ''}`}
                    onClick={() => toggleSoundNotifications()}
                    basic
                    style={{
                        [isRtl ? "marginRight" : "marginLeft"]: 20
                    }}
                />
                <Container className="last-update-container">
                    <Label>
                        {i18next.t('lastUpdate')}
                        {ordersUpdateDate ? ordersUpdateDate.toLocaleTimeString() : ''}
                    </Label>
                </Container>
            </div>

            <DataGrid
                data={orders}
                columns={columns}
                getTrGroupProps={(state, rowInfo, column) => {
                    if (typeof rowInfo !== "undefined") {
                        const order = rowInfo.original;

                        if (order.closeDate !== null) {
                            return { className: 'pi-data-grid-row-positive' };
                        }

                        if (order.isNew) {
                            return { className: 'pi-data-grid-row-warning' };
                        }

                        return {};
                    }

                    return {};
                }}
                paginated
               
            />
        </div>
    );
};

Orders.propTypes = {
    restaurant: PropTypes.object,
    orders: PropTypes.array,
    ordersUpdateDate: PropTypes.any,
    fetchActiveOrders: PropTypes.func,
    history: PropTypes.object,
    location: PropTypes.object,
    toggleSoundNotifications: PropTypes.func,
    muteSoundNotifications: PropTypes.bool
};

export default Orders;
